import { RefObject, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface ScrollPosition {
    left: number;
    top: number;
}

export function useMainScrollRestoration(scrollRef: RefObject<HTMLElement>) {
    const { pathname, search } = useLocation();
    // A diary modal shares the scroll position of the page underneath it.
    const scrollKey = pathname.replace(/\/modal\/?$/, "") + search;
    const positions = useRef(new Map<string, ScrollPosition>());

    useLayoutEffect(() => {
        const previous = window.history.scrollRestoration;
        window.history.scrollRestoration = "manual";
        return () => { window.history.scrollRestoration = previous; };
    }, []);

    useLayoutEffect(() => {
        const container = scrollRef.current;
        if (!container) {
            return;
        }

        const position = positions.current.get(scrollKey) ?? { left: 0, top: 0 };
        positions.current.set(scrollKey, position);
        let restoring = true;
        let frame: number | undefined;

        const savePosition = () => {
            if (!restoring) {
                positions.current.set(scrollKey, { left: container.scrollLeft, top: container.scrollTop });
            }
        };

        const stopRestoring = () => {
            restoring = false;
            if (frame !== undefined) {
                cancelAnimationFrame(frame);
                frame = undefined;
            }
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };

        const restorePosition = () => {
            frame = undefined;
            container.scrollTo({ left: position.left, top: position.top, behavior: "auto" });
            if (Math.abs(container.scrollTop - position.top) < 1 &&
                Math.abs(container.scrollLeft - position.left) < 1) {
                stopRestoring();
                savePosition();
            }
        };

        // Wait for route/tab effects, then retry if lazy content is still too short.
        const scheduleRestore = () => {
            if (restoring && frame === undefined) {
                frame = requestAnimationFrame(restorePosition);
            }
        };

        const resizeObserver = new ResizeObserver(scheduleRestore);
        const observeContent = () => {
            resizeObserver.disconnect();
            resizeObserver.observe(container);
            Array.from(container.children).forEach(child => resizeObserver.observe(child));
        };
        const mutationObserver = new MutationObserver(() => {
            observeContent();
            scheduleRestore();
        });

        const cancelRestoration = () => {
            stopRestoring();
            savePosition();
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) {
                cancelRestoration();
            }
        };

        observeContent();
        mutationObserver.observe(container, { childList: true, subtree: true });
        container.addEventListener("scroll", savePosition, { passive: true });
        container.addEventListener("wheel", cancelRestoration, { passive: true });
        container.addEventListener("touchmove", cancelRestoration, { passive: true });
        container.addEventListener("pointerdown", cancelRestoration, { passive: true });
        container.addEventListener("keydown", onKeyDown);
        // Navigation can happen before the browser delivers the latest scroll event.
        window.addEventListener("click", savePosition, true);
        window.addEventListener("popstate", savePosition);
        scheduleRestore();

        return () => {
            // Scroll events retain the outgoing position before the next route can shrink the DOM.
            stopRestoring();
            container.removeEventListener("scroll", savePosition);
            container.removeEventListener("wheel", cancelRestoration);
            container.removeEventListener("touchmove", cancelRestoration);
            container.removeEventListener("pointerdown", cancelRestoration);
            container.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("click", savePosition, true);
            window.removeEventListener("popstate", savePosition);
        };
    }, [scrollRef, scrollKey]);
}
